import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { loginSettingsAPI } from '../../services/api';

const LoginSettingsTab = () => {
  const { hasPermission } = useAuth();
  
  // State
  const [slides, setSlides] = useState([]);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [sliderConfig, setSliderConfig] = useState({ interval: 8000, autoPlay: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [slideForm, setSlideForm] = useState({
    title: '',
    description: '',
    stats: { label: '', value: '', icon: '📦' },
    chart: { percent: '', label: '' },
    isActive: true
  });

  // Permissions
  const canView = hasPermission('system.login_settings.view');
  const canEdit = hasPermission('system.login_settings.edit');

  // Fetch data on mount
  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [slidesRes, logoRes, configRes] = await Promise.all([
        loginSettingsAPI.getAllSlides(),
        loginSettingsAPI.getLogo(),
        loginSettingsAPI.getSliderConfig()
      ]);

      if (slidesRes.success) setSlides(slidesRes.data);
      if (logoRes.success) setLogoUrl(logoRes.data.logoUrl);
      if (configRes.success) setSliderConfig(configRes.data);
      
    } catch (err) {
      setError('Мэдээлэл татахад алдаа гарлаа');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Logo upload
  const handleLogoUpload = async (e) => {
    if (!canEdit) return;
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const response = await loginSettingsAPI.uploadLogo(file);
      
      if (response.success) {
        setLogoUrl(response.data.logoUrl);
        setSuccess('Лого амжилттай хадгалагдлаа');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Лого хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Slider config save
  const handleConfigSave = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      const response = await loginSettingsAPI.updateSliderConfig(sliderConfig);
      
      if (response.success) {
        setSuccess('Тохиргоо хадгалагдлаа');
      }
    } catch (err) {
      setError('Тохиргоо хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Open modal for new slide
  const handleAddSlide = () => {
    if (!canEdit) return;
    setEditingSlide(null);
    setSlideForm({
      title: '',
      description: '',
      stats: { label: '', value: '', icon: '📦' },
      chart: { percent: '', label: '' },
      isActive: true
    });
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditSlide = (slide) => {
    if (!canEdit) return;
    setEditingSlide(slide);
    setSlideForm({
      title: slide.title,
      description: slide.description,
      stats: slide.stats || { label: '', value: '', icon: '📦' },
      chart: slide.chart || { percent: '', label: '' },
      isActive: slide.isActive
    });
    setShowModal(true);
  };

  // Save slide
  const handleSaveSlide = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      
      if (editingSlide) {
        const response = await loginSettingsAPI.updateSlide(editingSlide._id, slideForm);
        if (response.success) {
          setSlides(slides.map(s => s._id === editingSlide._id ? response.data : s));
          setSuccess('Слайд шинэчлэгдлээ');
        }
      } else {
        const response = await loginSettingsAPI.createSlide({ ...slideForm, order: slides.length });
        if (response.success) {
          setSlides([...slides, response.data]);
          setSuccess('Слайд үүсгэлээ');
        }
      }
      
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Delete slide
  const handleDeleteSlide = async (id) => {
    if (!canEdit) return;
    if (!confirm('Энэ слайдыг устгах уу?')) return;
    
    try {
      const response = await loginSettingsAPI.deleteSlide(id);
      if (response.success) {
        setSlides(slides.filter(s => s._id !== id));
        setSuccess('Слайд устгалаа');
      }
    } catch (err) {
      setError('Устгахад алдаа гарлаа');
    }
  };

  // Toggle slide active status
  const handleToggleActive = async (slide) => {
    if (!canEdit) return;
    try {
      const response = await loginSettingsAPI.updateSlide(slide._id, { isActive: !slide.isActive });
      if (response.success) {
        setSlides(slides.map(s => s._id === slide._id ? response.data : s));
      }
    } catch (err) {
      setError('Төлөв өөрчлөхөд алдаа гарлаа');
    }
  };

  // Move slide up/down
  const handleMoveSlide = async (index, direction) => {
    if (!canEdit) return;
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= slides.length) return;
    
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    
    try {
      await loginSettingsAPI.reorderSlides(newSlides.map(s => s._id));
      setSlides(newSlides);
    } catch (err) {
      setError('Дараалал өөрчлөхөд алдаа гарлаа');
    }
  };

  // Get full logo URL - ЗАСВАРЛАСАН! 🎯
  const getLogoSrc = () => {
    console.log('🖼️ Raw logoUrl:', logoUrl);
    
    if (!logoUrl) return '/logo.png';
    
    // Remove quotes if JSON string
    let cleanUrl = logoUrl;
    if (typeof logoUrl === 'string') {
      cleanUrl = cleanUrl.replace(/^["']|["']$/g, '').trim();
    }
    
    console.log('🧹 Cleaned URL:', cleanUrl);
    
    // If full URL with http/https
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      console.log('✅ Using full HTTP URL:', cleanUrl);
      return cleanUrl;
    }
    
    // If it's from backend uploads folder
    if (cleanUrl.startsWith('/uploads')) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
      // Remove /api/v1 or /api from the end to get base URL
      const baseUrl = apiUrl.replace(/\/api(\/v1)?$/, '');
      const fullUrl = `${baseUrl}${cleanUrl}`;
      console.log('✅ Backend upload URL:', fullUrl);
      return fullUrl;
    }
    
    // Public folder file (relative path)
    console.log('✅ Public folder URL:', cleanUrl);
    return cleanUrl;
  };

  // Permission check
  if (!canView) {
    return (
      <div className="text-center py-12">
        <span className="text-6xl">🔒</span>
        <h3 className="mt-4 text-lg font-medium text-gray-900">Хандах эрхгүй</h3>
        <p className="mt-2 text-gray-500">Танд Login тохиргоо харах эрх байхгүй байна</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-500">Уншиж байна...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
          <span>✅</span> {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <span>❌</span> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Section */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <span>🖼️</span> Лого
          </h3>
          
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white">
              <img 
                src={getLogoSrc()} 
                alt="Logo" 
                className="max-w-full max-h-full object-contain"
                onError={(e) => { 
                  console.error('❌ Logo татахад алдаа:', e.target.src);
                  e.target.src = '/logo.png'; 
                }}
                onLoad={() => {
                  console.log('✅ Logo амжилттай татагдлаа:', getLogoSrc());
                }}
              />
            </div>
            
            <div>
              {canEdit ? (
                <label className="block">
                  <span className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 transition font-medium">
                    {saving ? 'Хадгалж байна...' : 'Лого солих'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={saving}
                  />
                </label>
              ) : (
                <span className="text-gray-500 text-sm">Засах эрхгүй</span>
              )}
              <p className="text-sm text-gray-500 mt-2">
                PNG, JPG, SVG (max 2MB)
              </p>
            </div>
          </div>
        </div>

        {/* Slider Config */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <span>⚙️</span> Slider тохиргоо
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Хугацаа (секунд)
              </label>
              <input
                type="number"
                value={sliderConfig.interval / 1000}
                onChange={(e) => setSliderConfig({
                  ...sliderConfig,
                  interval: parseInt(e.target.value) * 1000
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="3"
                max="30"
                disabled={!canEdit}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoPlay"
                checked={sliderConfig.autoPlay}
                onChange={(e) => setSliderConfig({
                  ...sliderConfig,
                  autoPlay: e.target.checked
                })}
                className="w-4 h-4 text-primary-600 rounded"
                disabled={!canEdit}
              />
              <label htmlFor="autoPlay" className="text-sm text-gray-700">
                Автомат эргэлт
              </label>
            </div>
            
            {canEdit && (
              <button
                onClick={handleConfigSave}
                disabled={saving}
                className="w-full bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition font-medium"
              >
                {saving ? 'Хадгалж байна...' : 'Хадгалах'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Slides Section */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <span>📑</span> Слайдууд
          </h3>
          {canEdit && (
            <button
              onClick={handleAddSlide}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
            >
              <span>➕</span> Шинэ слайд
            </button>
          )}
        </div>

        {slides.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-5xl">📭</span>
            <p className="text-gray-500 mt-4">Слайд байхгүй байна</p>
            {canEdit && (
              <button
                onClick={handleAddSlide}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Шинэ слайд нэмэх
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {slides.map((slide, index) => (
              <div
                key={slide._id}
                className={`bg-white border rounded-lg p-4 ${
                  slide.isActive ? 'border-gray-200' : 'border-gray-300 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-2xl">{slide.stats?.icon || '📦'}</span>
                      <h4 className="font-semibold text-gray-900">{slide.title}</h4>
                      {!slide.isActive && (
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                          Идэвхгүй
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                      {slide.description}
                    </p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                      <span>📊 {slide.stats?.label}: {slide.stats?.value}</span>
                      <span>📈 {slide.chart?.percent}</span>
                    </div>
                  </div>
                  
                  {canEdit && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Move buttons */}
                      <button
                        onClick={() => handleMoveSlide(index, 'up')}
                        disabled={index === 0}
                        className="p-2 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                        title="Дээш"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleMoveSlide(index, 'down')}
                        disabled={index === slides.length - 1}
                        className="p-2 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                        title="Доош"
                      >
                        ↓
                      </button>
                      
                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggleActive(slide)}
                        className={`p-2 rounded transition ${
                          slide.isActive 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={slide.isActive ? 'Идэвхгүй болгох' : 'Идэвхжүүлэх'}
                      >
                        {slide.isActive ? '👁️' : '👁️‍🗨️'}
                      </button>
                      
                      {/* Edit */}
                      <button
                        onClick={() => handleEditSlide(slide)}
                        className="p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition"
                        title="Засах"
                      >
                        ✏️
                      </button>
                      
                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteSlide(slide._id)}
                        className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                        title="Устгах"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && canEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingSlide ? '✏️ Слайд засах' : '➕ Шинэ слайд'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Гарчиг <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={slideForm.title}
                  onChange={(e) => setSlideForm({ ...slideForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Тавтай морил!"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тайлбар <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={slideForm.description}
                  onChange={(e) => setSlideForm({ ...slideForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                  placeholder="Pingo Express нь таны бизнесийн..."
                />
              </div>
              
              {/* Stats */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium mb-3 text-gray-900">📊 Статистик карт</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Icon</label>
                    <input
                      type="text"
                      value={slideForm.stats.icon}
                      onChange={(e) => setSlideForm({
                        ...slideForm,
                        stats: { ...slideForm.stats, icon: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-center text-xl"
                      placeholder="📦"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Label</label>
                    <input
                      type="text"
                      value={slideForm.stats.label}
                      onChange={(e) => setSlideForm({
                        ...slideForm,
                        stats: { ...slideForm.stats, label: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder="НИЙТ ХҮРГЭЛТ"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Value</label>
                    <input
                      type="text"
                      value={slideForm.stats.value}
                      onChange={(e) => setSlideForm({
                        ...slideForm,
                        stats: { ...slideForm.stats, value: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder="24,359"
                    />
                  </div>
                </div>
              </div>
              
              {/* Chart */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium mb-3 text-gray-900">📈 График</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Хувь</label>
                    <input
                      type="text"
                      value={slideForm.chart.percent}
                      onChange={(e) => setSlideForm({
                        ...slideForm,
                        chart: { ...slideForm.chart, percent: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder="34%"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Label</label>
                    <input
                      type="text"
                      value={slideForm.chart.label}
                      onChange={(e) => setSlideForm({
                        ...slideForm,
                        chart: { ...slideForm.chart, label: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder="Хүргэлт"
                    />
                  </div>
                </div>
              </div>
              
              {/* Active */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="slideIsActive"
                  checked={slideForm.isActive}
                  onChange={(e) => setSlideForm({ ...slideForm, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="slideIsActive" className="text-sm text-gray-700">
                  Идэвхтэй
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3 bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
              >
                Болих
              </button>
              <button
                onClick={handleSaveSlide}
                disabled={saving || !slideForm.title || !slideForm.description}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition font-medium"
              >
                {saving ? 'Хадгалж байна...' : 'Хадгалах'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSettingsTab;